import React, { useState, useEffect } from 'react';
import axiosInstance from '../utils/axiosConfig.js';
import { formatApiError } from '../utils/apiErrorFormatter';
import LoadingSpinner from './LoadingSpinner';
import { FaTrash, FaExclamationTriangle } from 'react-icons/fa';
import '../styles/SpecialityInstitutionManager.css';

// get_full_specialities returns { _id, name, totalAvailability, availability?, groups } — use name for display
const getSpecialityDisplayName = (s) => (s && (s.name ?? s.speciality)) || '';
const getInstitutionDisplayName = (i) => (i && (i.name ?? i.institution)) || '';

const normalizeMessage = (value, fallback = 'Something went wrong. Please try again.') => {
    if (typeof value === 'string') {
        return value;
    }
    if (Array.isArray(value) && value.length > 0) {
        const first = value[0];
        if (typeof first === 'string') {
            return first;
        }
        if (first && typeof first.msg === 'string') {
            return first.msg;
        }
    }
    if (value && typeof value === 'object' && typeof value.msg === 'string') {
        return value.msg;
    }
    return fallback;
};

const SpecialityInstitutionManager = () => {
    const [activeTab, setActiveTab] = useState('specialities');
    const [specialities, setSpecialities] = useState([]);
    const [institutions, setInstitutions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingSpecialities, setLoadingSpecialities] = useState(true);
    const [loadingInstitutions, setLoadingInstitutions] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [expandedItem, setExpandedItem] = useState(null);
    const [editingItem, setEditingItem] = useState(null);
    
    // Form states
    const [newSpeciality, setNewSpeciality] = useState('');
    const [newInstitution, setNewInstitution] = useState('');
    const [specialityAvailability, setSpecialityAvailability] = useState([]);
    const [institutionAvailability, setInstitutionAvailability] = useState([]);
    
    // Group management for creation
    const [newSpecialityGroups, setNewSpecialityGroups] = useState([]);
    const [newInstitutionGroups, setNewInstitutionGroups] = useState([]);
    const [currentGroupInput, setCurrentGroupInput] = useState('');
    
    // Sub-Rotation/Sub-Experience and sub-institution management
    const [newSubSpecialities, setNewSubSpecialities] = useState([]);
    const [newSubInstitutions, setNewSubInstitutions] = useState([]);
    const [currentSubSpecialityInput, setCurrentSubSpecialityInput] = useState('');
    const [currentSubInstitutionInput, setCurrentSubInstitutionInput] = useState('');
    
    // Deletion confirmation modal
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [deleteType, setDeleteType] = useState('');
    const [deleteWarning, setDeleteWarning] = useState(null);
    const [replacementId, setReplacementId] = useState('');

    useEffect(() => {
        fetchSpecialities();
        fetchInstitutions();
    }, []);

    const showSuccess = (message) => {
        setSuccess(message);
        setTimeout(() => setSuccess(''), 3500);
    };

    const showError = (message) => {
        setError(normalizeMessage(message));
        setTimeout(() => setError(''), 3500);
    };

    const fetchSpecialities = async () => {
        setLoadingSpecialities(true);
        try {
            const response = await axiosInstance.get('/crud/get_full_specialities');
            setSpecialities(Array.isArray(response.data) ? response.data : []);
        } catch (err) {
            console.error('Error fetching specialities:', err);
            showError('Failed to fetch rotation types');
            setSpecialities([]);
        } finally {
            setLoadingSpecialities(false);
        }
    };

    const fetchInstitutions = async () => {
        setLoadingInstitutions(true);
        try {
            const response = await axiosInstance.get('/crud/get_full_institutions');
            setInstitutions(Array.isArray(response.data) ? response.data : []);
        } catch (err) {
            console.error('Error fetching institutions:', err);
            showError('Failed to fetch institutions');
            setInstitutions([]);
        } finally {
            setLoadingInstitutions(false);
        }
    };

    const createSpeciality = async (e) => {
        e.preventDefault();
        if (!newSpeciality.trim()) {
            showError('Please enter a Rotation Type name');
            return;
        }

        setLoading(true);
        try {
            const response = await axiosInstance.post('/crud/create_speciality/', null, {
                params: {
                    speciality: newSpeciality.trim(),
                    sub_specialities: newSubSpecialities
                }
            });

            if (response.data.status === 'success') {
                showSuccess(`Rotation Type "${newSpeciality}" created successfully!`);
                setNewSpeciality('');
                setNewSpecialityGroups([]);
                setNewSubSpecialities([]);
                setCurrentGroupInput('');
                setCurrentSubSpecialityInput('');
                await fetchSpecialities();
            }
        } catch (err) {
            console.error('Error creating speciality:', err);
            showError(formatApiError(err, 'Failed to create Rotation Type'));
        } finally {
            setLoading(false);
        }
    };

    const createInstitution = async (e) => {
        e.preventDefault();
        if (!newInstitution.trim()) {
            showError('Please enter an institution name');
            return;
        }

        setLoading(true);
        try {
            const response = await axiosInstance.post('/crud/create_institution/', null, {
                params: {
                    name: newInstitution.trim(),
                    sub_institutions: newSubInstitutions
                }
            });

            if (response.data.status === 'success') {
                showSuccess(`Institution "${newInstitution}" created successfully!`);
                setNewInstitution('');
                setNewInstitutionGroups([]);
                setNewSubInstitutions([]);
                setCurrentGroupInput('');
                setCurrentSubInstitutionInput('');
                await fetchInstitutions();
            }
        } catch (err) {
            console.error('Error creating institution:', err);
            showError(formatApiError(err, 'Failed to create institution'));
        } finally {
            setLoading(false);
        }
    };

    const editSpeciality = async (id, updatedData) => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('_id', id);
            params.append('speciality', updatedData.speciality);
            
            if (updatedData.groups && updatedData.groups.length > 0) {
                updatedData.groups.forEach(group => {
                    params.append('groups', group);
                });
            }
            
            if (updatedData.subSpecialities && updatedData.subSpecialities.length > 0) {
                updatedData.subSpecialities.forEach(subSpec => {
                    params.append('subSpecialities', subSpec);
                });
            }

            const response = await axiosInstance.put('/crud/edit_speciality', params, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            });

            if (response.data.status === 'success') {
                showSuccess(`Rotation Type "${updatedData.speciality}" updated successfully!`);
                setEditingItem(null);
                setExpandedItem(null);
                await fetchSpecialities();
            }
        } catch (err) {
            console.error('Error updating speciality:', err);
            showError(formatApiError(err, 'Failed to update Rotation Type'));
        } finally {
            setLoading(false);
        }
    };

    const editInstitution = async (id, updatedData) => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('_id', id);
            params.append('institution', updatedData.institution);
            
            if (updatedData.groups && updatedData.groups.length > 0) {
                updatedData.groups.forEach(group => {
                    params.append('groups', group);
                });
            }
            
            if (updatedData.subInstitutions && updatedData.subInstitutions.length > 0) {
                updatedData.subInstitutions.forEach(subInst => {
                    params.append('subInstitutions', subInst);
                });
            }

            const response = await axiosInstance.put('/crud/edit_institution', params, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            });

            if (response.data.status === 'success') {
                showSuccess(`Institution "${updatedData.institution}" updated successfully!`);
                setEditingItem(null);
                setExpandedItem(null);
                await fetchInstitutions();
            }
        } catch (err) {
            console.error('Error updating institution:', err);
            showError(formatApiError(err, 'Failed to update institution'));
        } finally {
            setLoading(false);
        }
    };

    const deleteSpeciality = async (id) => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('_id', id);

            const response = await axiosInstance.delete('/crud/delete_speciality', {
                data: params,
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            });

            if (response.data.status === 'success') {
                showSuccess('Rotation Type deleted successfully!');
                setExpandedItem(null);
                setEditingItem(null);
                await fetchSpecialities();
            }
        } catch (err) {
            console.error('Error deleting speciality:', err);
            showError(formatApiError(err, 'Failed to delete Rotation Type'));
        } finally {
            setLoading(false);
        }
    };

    const deleteInstitution = async (id) => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('_id', id);

            const response = await axiosInstance.delete('/crud/delete_institution', {
                data: params,
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            });

            if (response.data.status === 'success') {
                showSuccess('Institution deleted successfully!');
                setExpandedItem(null);
                setEditingItem(null);
                await fetchInstitutions();
            }
        } catch (err) {
            console.error('Error deleting institution:', err);
            showError(formatApiError(err, 'Failed to delete institution'));
        } finally {
            setLoading(false);
        }
    };

    const handleItemClick = (item, type) => {
        if (expandedItem === item._id) {
            setExpandedItem(null);
            setEditingItem(null);
        } else {
            setExpandedItem(item._id);
            setEditingItem(null);
        }
    };

    const handleEditClick = (item, type) => {
        setEditingItem({
            ...item,
            type: type,
            speciality: type === 'speciality' ? (item.name ?? item.speciality) : item.speciality,
            name: item.name ?? item.institution,
            institution: item.name ?? item.institution,
            groups: item.groups || [],
            subSpecialities: item.subSpecialities || [],
            subInstitutions: item.subInstitutions || []
        });
    };

    const handleSaveEdit = () => {
        if (editingItem.type === 'speciality') {
            editSpeciality(editingItem._id, {
                speciality: editingItem.speciality,
                groups: editingItem.groups,
                subSpecialities: editingItem.subSpecialities
            });
        } else {
            editInstitution(editingItem._id, {
                institution: editingItem.name || editingItem.institution,
                groups: editingItem.groups,
                subInstitutions: editingItem.subInstitutions
            });
        }
    };

    const handleCancelEdit = () => {
        setEditingItem(null);
    };

    const addGroupToEdit = (group) => {
        if (group.trim() && !editingItem.groups.includes(group.trim())) {
            setEditingItem({
                ...editingItem,
                groups: [...editingItem.groups, group.trim()]
            });
        }
    };

    const removeGroupFromEdit = (group) => {
        setEditingItem({
            ...editingItem,
            groups: editingItem.groups.filter(g => g !== group)
        });
    };

    const addAvailabilityNumber = (type, value) => {
        const numValue = parseInt(value);
        if (isNaN(numValue) || numValue < 0) return;

        if (type === 'speciality') {
            if (!specialityAvailability.includes(numValue)) {
                setSpecialityAvailability([...specialityAvailability, numValue].sort((a, b) => a - b));
            }
        } else {
            if (!institutionAvailability.includes(numValue)) {
                setInstitutionAvailability([...institutionAvailability, numValue].sort((a, b) => a - b));
            }
        }
    };

    const removeAvailabilityNumber = (type, value) => {
        if (type === 'speciality') {
            setSpecialityAvailability(specialityAvailability.filter(num => num !== value));
        } else {
            setInstitutionAvailability(institutionAvailability.filter(num => num !== value));
        }
    };

    // Group management for creation forms
    const addGroupToCreation = (type, group) => {
        if (!group.trim()) return;
        
        if (type === 'speciality') {
            if (!newSpecialityGroups.includes(group.trim())) {
                setNewSpecialityGroups([...newSpecialityGroups, group.trim()]);
            }
        } else {
            if (!newInstitutionGroups.includes(group.trim())) {
                setNewInstitutionGroups([...newInstitutionGroups, group.trim()]);
            }
        }
        setCurrentGroupInput('');
    };

    const removeGroupFromCreation = (type, group) => {
        if (type === 'speciality') {
            setNewSpecialityGroups(newSpecialityGroups.filter(g => g !== group));
        } else {
            setNewInstitutionGroups(newInstitutionGroups.filter(g => g !== group));
        }
    };

    // Sub-Rotation/Sub-Experience and sub-institution management for creation forms
    const addSubSpecialityToCreation = (subSpec) => {
        if (!subSpec.trim()) return;
        if (!newSubSpecialities.includes(subSpec.trim())) {
            setNewSubSpecialities([...newSubSpecialities, subSpec.trim()]);
        }
        setCurrentSubSpecialityInput('');
    };

    const removeSubSpecialityFromCreation = (subSpec) => {
        setNewSubSpecialities(newSubSpecialities.filter(s => s !== subSpec));
    };

    const addSubInstitutionToCreation = (subInst) => {
        if (!subInst.trim()) return;
        if (!newSubInstitutions.includes(subInst.trim())) {
            setNewSubInstitutions([...newSubInstitutions, subInst.trim()]);
        }
        setCurrentSubInstitutionInput('');
    };

    const removeSubInstitutionFromCreation = (subInst) => {
        setNewSubInstitutions(newSubInstitutions.filter(s => s !== subInst));
    };

    // Sub-Rotation/Sub-Experience and sub-institution management for editing
    const addSubSpecialityToEdit = (subSpec) => {
        if (subSpec.trim() && !editingItem.subSpecialities.includes(subSpec.trim())) {
            setEditingItem({
                ...editingItem,
                subSpecialities: [...(editingItem.subSpecialities || []), subSpec.trim()]
            });
        }
    };

    const removeSubSpecialityFromEdit = (subSpec) => {
        setEditingItem({
            ...editingItem,
            subSpecialities: (editingItem.subSpecialities || []).filter(s => s !== subSpec)
        });
    };

    const addSubInstitutionToEdit = (subInst) => {
        if (subInst.trim() && !editingItem.subInstitutions.includes(subInst.trim())) {
            setEditingItem({
                ...editingItem,
                subInstitutions: [...(editingItem.subInstitutions || []), subInst.trim()]
            });
        }
    };

    const removeSubInstitutionFromEdit = (subInst) => {
        setEditingItem({
            ...editingItem,
            subInstitutions: (editingItem.subInstitutions || []).filter(s => s !== subInst)
        });
    };

    // Deletion confirmation
    const handleDeleteClick = (item, type) => {
        setItemToDelete(item);
        setDeleteType(type);
        setDeleteWarning(null);
        setReplacementId('');
        setShowDeleteModal(true);
    };

    const getReplacementOptions = () => {
        if (!itemToDelete || !deleteType) {
            return [];
        }

        if (deleteType === 'speciality') {
            return specialities.filter((speciality) => speciality._id !== itemToDelete._id);
        }

        return institutions.filter((institution) => institution._id !== itemToDelete._id);
    };

    const confirmDelete = async () => {
        if (!itemToDelete || !deleteType) {
            return;
        }

        const selectedReplacement = deleteWarning ? replacementId.trim() : '';
        if (deleteWarning && !selectedReplacement) {
            showError(`Please select a replacement ${deleteType} before continuing.`);
            return;
        }

        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('_id', itemToDelete._id);
            if (selectedReplacement) {
                params.append('replacement', selectedReplacement);
            }

            const response = await axiosInstance.delete(
                deleteType === 'speciality' ? '/crud/delete_speciality' : '/crud/delete_institution',
                { params }
            );

            if (response.data?.status === 'warning' || response.data?.requires_replacement) {
                setDeleteWarning(response.data);
                return;
            }

            if (response.data?.status === 'deleted') {
                showSuccess(
                    deleteType === 'speciality'
                        ? 'Rotation Type deleted successfully!'
                        : 'Institution deleted successfully!'
                );
                setShowDeleteModal(false);
                setItemToDelete(null);
                setDeleteType('');
                setDeleteWarning(null);
                setReplacementId('');
                setExpandedItem(null);
                setEditingItem(null);
                if (deleteType === 'speciality') {
                    await fetchSpecialities();
                } else {
                    await fetchInstitutions();
                }
                return;
            }

            throw new Error('Unexpected delete response');
        } catch (err) {
            console.error('Error deleting item:', err);
            showError(formatApiError(err, `Failed to delete ${deleteType === 'speciality' ? 'Rotation Type' : 'institution'}`));
        } finally {
            setLoading(false);
        }
    };

    const cancelDelete = () => {
        setShowDeleteModal(false);
        setItemToDelete(null);
        setDeleteType('');
        setDeleteWarning(null);
        setReplacementId('');
    };

    const renderSpecialitiesTab = () => (
        <div className="tab-content">
            <div className="create-form-section">
                <h3 className="section-title">Create New Rotation Type</h3>
                <form onSubmit={createSpeciality} className="create-form">
                    <div className="form-group">
                        <label htmlFor="speciality-name" className="form-label">
                            Rotation Type Name *
                        </label>
                        <input
                            id="speciality-name"
                            type="text"
                            value={newSpeciality}
                            onChange={(e) => setNewSpeciality(e.target.value)}
                            placeholder="Enter Rotation Type name (e.g., Internal Medicine)"
                            className="form-input"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">
                            Groups (Optional)
                        </label>
                        <div className="group-input-section">
                            <input
                                type="text"
                                value={currentGroupInput}
                                onChange={(e) => setCurrentGroupInput(e.target.value)}
                                placeholder="Add a group/tag"
                                className="form-input group-input"
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        addGroupToCreation('speciality', currentGroupInput);
                                    }
                                }}
                            />
                            <button
                                type="button"
                                className="add-group-btn"
                                onClick={() => addGroupToCreation('speciality', currentGroupInput)}
                            >
                                Add
                            </button>
                        </div>
                        {newSpecialityGroups.length > 0 && (
                            <div className="group-tags">
                                {newSpecialityGroups.map((group, index) => (
                                    <span key={index} className="group-tag">
                                        {group}
                                        <button
                                            type="button"
                                            onClick={() => removeGroupFromCreation('speciality', group)}
                                            className="remove-group-btn"
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="form-group">
                        <label className="form-label">
                            Sub-Rotation Types (Optional)
                        </label>
                        <div className="group-input-section">
                            <input
                                type="text"
                                value={currentSubSpecialityInput}
                                onChange={(e) => setCurrentSubSpecialityInput(e.target.value)}
                                placeholder="Add a sub-Rotation Type"
                                className="form-input group-input"
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        addSubSpecialityToCreation(currentSubSpecialityInput);
                                    }
                                }}
                            />
                            <button
                                type="button"
                                className="add-group-btn"
                                onClick={() => addSubSpecialityToCreation(currentSubSpecialityInput)}
                            >
                                Add
                            </button>
                        </div>
                        {newSubSpecialities.length > 0 && (
                            <div className="group-tags">
                                {newSubSpecialities.map((subSpec, index) => (
                                    <span key={index} className="group-tag">
                                        {subSpec}
                                        <button
                                            type="button"
                                            onClick={() => removeSubSpecialityFromCreation(subSpec)}
                                            className="remove-group-btn"
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    <button
                        type="submit"
                        disabled={loading || !newSpeciality.trim()}
                        className="submit-btn"
                    >
                        {loading ? 'Creating...' : 'Create Rotation Type'}
                    </button>
                </form>
            </div>

            <div className="list-section">
                <h3 className="section-title">Existing Rotations/Experiences ({specialities.length})</h3>
                <div className={`items-grid ${loadingSpecialities ? 'items-grid--loading' : ''}`}>
                    {loadingSpecialities ? (
                        <div className="loading-state">
                            <LoadingSpinner text="Loading rotations/experiences..." variant="inline" />
                        </div>
                    ) : (
                    <>
                    {specialities.map((speciality) => (
                        <div key={speciality._id} className={`modern-item-card ${expandedItem === speciality._id ? 'expanded' : ''}`}>
                            <div className="item-preview" onClick={() => handleItemClick(speciality, 'speciality')}>
                                <div className="item-header">
                                    <div className="item-main-info">
                                        <h4 className="item-name">{getSpecialityDisplayName(speciality)}</h4>
                                    </div>
                                    <div className="header-actions">
                                        <button 
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteClick(speciality, 'speciality');
                                            }} 
                                            className="delete-btn-mini"
                                            title="Delete Rotation Type"
                                        >
                                            🗑️
                                        </button>
                                        <div className="expand-indicator">
                                            {expandedItem === speciality._id ? '▲' : '▼'}
                                        </div>
                                    </div>
                                </div>
                                {speciality.groups && speciality.groups.length > 0 && (
                                    <div className="item-groups-preview">
                                        <span className="groups-label">Rotation Type Groups:</span>
                                        {speciality.groups.slice(0, 3).map((group, index) => (
                                            <span key={index} className="group-chip-mini">{group}</span>
                                        ))}
                                        {speciality.groups.length > 3 && (
                                            <span className="more-groups">+{speciality.groups.length - 3} more</span>
                                        )}
                                    </div>
                                )}
                            </div>
                            
                            {expandedItem === speciality._id && (
                                <div className="item-details">
                                    {editingItem && editingItem._id === speciality._id ? (
                                        <div className="edit-form">
                                            <div className="edit-header">
                                                <h4>Edit Rotation Type</h4>
                                                <div className="edit-actions">
                                                    <button onClick={handleSaveEdit} disabled={loading} className="save-btn">
                                                        {loading ? 'Saving...' : 'Save'}
                                                    </button>
                                                    <button onClick={handleCancelEdit} className="cancel-btn">Cancel</button>
                                                </div>
                                            </div>
                                            
                                            <div className="form-group">
                                                <label className="form-label">Rotation Type Name</label>
                                                <input
                                                    type="text"
                                                    value={editingItem.speciality}
                                                    onChange={(e) => setEditingItem({...editingItem, speciality: e.target.value})}
                                                    className="form-input"
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label className="form-label">Groups/Tags</label>
                                                <div className="group-input-section">
                                                    <input
                                                        type="text"
                                                        placeholder="Add a group/tag"
                                                        className="form-input group-input"
                                                        onKeyPress={(e) => {
                                                            if (e.key === 'Enter') {
                                                                e.preventDefault();
                                                                addGroupToEdit(e.target.value);
                                                                e.target.value = '';
                                                            }
                                                        }}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="add-group-btn"
                                                        onClick={(e) => {
                                                            const input = e.target.previousElementSibling;
                                                            addGroupToEdit(input.value);
                                                            input.value = '';
                                                        }}
                                                    >
                                                        Add
                                                    </button>
                                                </div>
                                                {editingItem.groups.length > 0 && (
                                                    <div className="group-tags">
                                                        {editingItem.groups.map((group, index) => (
                                                            <span key={index} className="group-tag">
                                                                {group}
                                                                <button
                                                                    type="button"
                                                                    onClick={() => removeGroupFromEdit(group)}
                                                                    className="remove-tag-btn"
                                                                >
                                                                    ×
                                                                </button>
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="form-group">
                                                <label className="form-label">Sub-Rotation Types</label>
                                                <div className="group-input-section">
                                                    <input
                                                        type="text"
                                                        placeholder="Add a Sub-Rotation/Sub-Experience"
                                                        className="form-input group-input"
                                                        onKeyPress={(e) => {
                                                            if (e.key === 'Enter') {
                                                                e.preventDefault();
                                                                addSubSpecialityToEdit(e.target.value);
                                                                e.target.value = '';
                                                            }
                                                        }}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="add-group-btn"
                                                        onClick={(e) => {
                                                            const input = e.target.previousElementSibling;
                                                            addSubSpecialityToEdit(input.value);
                                                            input.value = '';
                                                        }}
                                                    >
                                                        Add
                                                    </button>
                                                </div>
                                                {editingItem.subSpecialities && editingItem.subSpecialities.length > 0 && (
                                                    <div className="group-tags">
                                                        {editingItem.subSpecialities.map((subSpec, index) => (
                                                            <span key={index} className="group-tag">
                                                                {subSpec}
                                                                <button
                                                                    type="button"
                                                                    onClick={() => removeSubSpecialityFromEdit(subSpec)}
                                                                    className="remove-tag-btn"
                                                                >
                                                                    ×
                                                                </button>
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="detail-view">
                                            <div className="detail-header">
                                                <h4>Rotation Type Details</h4>
                                                <button 
                                                    onClick={() => handleEditClick(speciality, 'speciality')} 
                                                    className="edit-btn"
                                                >
                                                    ✏️ Edit
                                                </button>
                                            </div>
                                            
                                            <div className="detail-grid">
                                                <div className="detail-section">
                                                    <h5>Basic Information</h5>
                                                    <div className="detail-item">
                                                        <span className="detail-label">Name:</span>
                                                        <span className="detail-value">{getSpecialityDisplayName(speciality)}</span>
                                                    </div>
                                                </div>

                                                {speciality.totalAvailability && speciality.totalAvailability.length > 0 && (
                                                    <div className="detail-section">
                                                        <h5>Availability Slots</h5>
                                                        <div className="availability-display">
                                                            {speciality.totalAvailability.map((num, index) => (
                                                                <span key={index} className="availability-chip">{num}</span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {speciality.groups && speciality.groups.length > 0 && (
                                                    <div className="detail-section">
                                                        <h5>Groups/Tags</h5>
                                                        <div className="groups-display">
                                                            {speciality.groups.map((group, index) => (
                                                                <span key={index} className="group-chip">{group}</span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {speciality.subSpecialities && speciality.subSpecialities.length > 0 && (
                                                    <div className="detail-section">
                                                        <h5>Sub-Rotation/Sub-Experiences</h5>
                                                        <div className="groups-display">
                                                            {speciality.subSpecialities.map((subSpec, index) => (
                                                                <span key={index} className="group-chip sub-speciality-chip">{subSpec}</span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                    {!loadingSpecialities && specialities.length === 0 && (
                        <div className="empty-state">
                            <div className="empty-icon">🏥</div>
                            <p>No Rotation Types found. Create your first Rotation Type above.</p>
                        </div>
                    )}
                    </>
                    )}
                </div>
            </div>
        </div>
    );

    const renderInstitutionsTab = () => (
        <div className="tab-content">
            <div className="create-form-section">
                <h3 className="section-title">Create New Institution</h3>
                <form onSubmit={createInstitution} className="create-form">
                    <div className="form-group">
                        <label htmlFor="institution-name" className="form-label">
                            Institution Name *
                        </label>
                        <input
                            id="institution-name"
                            type="text"
                            value={newInstitution}
                            onChange={(e) => setNewInstitution(e.target.value)}
                            placeholder="Enter institution name (e.g., Qatar University Hospital)"
                            className="form-input"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">
                            Groups (Optional)
                        </label>
                        <div className="group-input-section">
                            <input
                                type="text"
                                value={currentGroupInput}
                                onChange={(e) => setCurrentGroupInput(e.target.value)}
                                placeholder="Add a group/tag"
                                className="form-input group-input"
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        addGroupToCreation('institution', currentGroupInput);
                                    }
                                }}
                            />
                            <button
                                type="button"
                                className="add-group-btn"
                                onClick={() => addGroupToCreation('institution', currentGroupInput)}
                            >
                                Add
                            </button>
                        </div>
                        {newInstitutionGroups.length > 0 && (
                            <div className="group-tags">
                                {newInstitutionGroups.map((group, index) => (
                                    <span key={index} className="group-tag">
                                        {group}
                                        <button
                                            type="button"
                                            onClick={() => removeGroupFromCreation('institution', group)}
                                            className="remove-group-btn"
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="form-group">
                        <label className="form-label">
                            Sub-Institutions (Optional)
                        </label>
                        <div className="group-input-section">
                            <input
                                type="text"
                                value={currentSubInstitutionInput}
                                onChange={(e) => setCurrentSubInstitutionInput(e.target.value)}
                                placeholder="Add a sub-institution"
                                className="form-input group-input"
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        addSubInstitutionToCreation(currentSubInstitutionInput);
                                    }
                                }}
                            />
                            <button
                                type="button"
                                className="add-group-btn"
                                onClick={() => addSubInstitutionToCreation(currentSubInstitutionInput)}
                            >
                                Add
                            </button>
                        </div>
                        {newSubInstitutions.length > 0 && (
                            <div className="group-tags">
                                {newSubInstitutions.map((subInst, index) => (
                                    <span key={index} className="group-tag">
                                        {subInst}
                                        <button
                                            type="button"
                                            onClick={() => removeSubInstitutionFromCreation(subInst)}
                                            className="remove-group-btn"
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    <button
                        type="submit"
                        disabled={loading || !newInstitution.trim()}
                        className="submit-btn"
                    >
                        {loading ? 'Creating...' : 'Create Institution'}
                    </button>
                </form>
            </div>

            <div className="list-section">
                <h3 className="section-title">Existing Institutions ({institutions.length})</h3>
                <div className={`items-grid ${loadingInstitutions ? 'items-grid--loading' : ''}`}>
                    {loadingInstitutions ? (
                        <div className="loading-state">
                            <LoadingSpinner text="Loading institutions..." variant="inline" />
                        </div>
                    ) : (
                    <>
                    {institutions.map((institution) => (
                        <div key={institution._id} className={`modern-item-card ${expandedItem === institution._id ? 'expanded' : ''}`}>
                            <div className="item-preview" onClick={() => handleItemClick(institution, 'institution')}>
                                <div className="item-header">
                                    <div className="item-main-info">
                                        <h4 className="item-name">{getInstitutionDisplayName(institution)}</h4>
                                    </div>
                                    <div className="header-actions">
                                        <button 
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteClick(institution, 'institution');
                                            }} 
                                            className="delete-btn-mini"
                                            title="Delete Institution"
                                        >
                                            🗑️
                                        </button>
                                        <div className="expand-indicator">
                                            {expandedItem === institution._id ? '▲' : '▼'}
                                        </div>
                                    </div>
                                </div>
                                {institution.groups && institution.groups.length > 0 && (
                                    <div className="item-groups-preview">
                                        <span className="groups-label">Institution Groups:</span>
                                        {institution.groups.slice(0, 3).map((group, index) => (
                                            <span key={index} className="group-chip-mini">{group}</span>
                                        ))}
                                        {institution.groups.length > 3 && (
                                            <span className="more-groups">+{institution.groups.length - 3} more</span>
                                        )}
                                    </div>
                                )}
                            </div>
                            
                            {expandedItem === institution._id && (
                                <div className="item-details">
                                    {editingItem && editingItem._id === institution._id ? (
                                        <div className="edit-form">
                                            <div className="edit-header">
                                                <h4>Edit Institution</h4>
                                                <div className="edit-actions">
                                                    <button onClick={handleSaveEdit} disabled={loading} className="save-btn">
                                                        {loading ? 'Saving...' : 'Save'}
                                                    </button>
                                                    <button onClick={handleCancelEdit} className="cancel-btn">Cancel</button>
                                                </div>
                                            </div>
                                            
                                            <div className="form-group">
                                                <label className="form-label">Institution Name</label>
                                                <input
                                                    type="text"
                                                    value={editingItem.name || editingItem.institution || ''}
                                                    onChange={(e) => setEditingItem({
                                                        ...editingItem, 
                                                        name: e.target.value,
                                                        institution: e.target.value
                                                    })}
                                                    className="form-input"
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label className="form-label">Groups/Tags</label>
                                                <div className="group-input-section">
                                                    <input
                                                        type="text"
                                                        placeholder="Add a group/tag"
                                                        className="form-input group-input"
                                                        onKeyPress={(e) => {
                                                            if (e.key === 'Enter') {
                                                                e.preventDefault();
                                                                addGroupToEdit(e.target.value);
                                                                e.target.value = '';
                                                            }
                                                        }}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="add-group-btn"
                                                        onClick={(e) => {
                                                            const input = e.target.previousElementSibling;
                                                            addGroupToEdit(input.value);
                                                            input.value = '';
                                                        }}
                                                    >
                                                        Add
                                                    </button>
                                                </div>
                                                {editingItem.groups.length > 0 && (
                                                    <div className="group-tags">
                                                        {editingItem.groups.map((group, index) => (
                                                            <span key={index} className="group-tag">
                                                                {group}
                                                                <button
                                                                    type="button"
                                                                    onClick={() => removeGroupFromEdit(group)}
                                                                    className="remove-tag-btn"
                                                                >
                                                                    ×
                                                                </button>
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="form-group">
                                                <label className="form-label">Sub-Institutions</label>
                                                <div className="group-input-section">
                                                    <input
                                                        type="text"
                                                        placeholder="Add a sub-institution"
                                                        className="form-input group-input"
                                                        onKeyPress={(e) => {
                                                            if (e.key === 'Enter') {
                                                                e.preventDefault();
                                                                addSubInstitutionToEdit(e.target.value);
                                                                e.target.value = '';
                                                            }
                                                        }}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="add-group-btn"
                                                        onClick={(e) => {
                                                            const input = e.target.previousElementSibling;
                                                            addSubInstitutionToEdit(input.value);
                                                            input.value = '';
                                                        }}
                                                    >
                                                        Add
                                                    </button>
                                                </div>
                                                {editingItem.subInstitutions && editingItem.subInstitutions.length > 0 && (
                                                    <div className="group-tags">
                                                        {editingItem.subInstitutions.map((subInst, index) => (
                                                            <span key={index} className="group-tag">
                                                                {subInst}
                                                                <button
                                                                    type="button"
                                                                    onClick={() => removeSubInstitutionFromEdit(subInst)}
                                                                    className="remove-tag-btn"
                                                                >
                                                                    ×
                                                                </button>
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="detail-view">
                                            <div className="detail-header">
                                                <h4>Institution Details</h4>
                                                <button 
                                                    onClick={() => handleEditClick(institution, 'institution')} 
                                                    className="edit-btn"
                                                >
                                                    ✏️ Edit
                                                </button>
                                            </div>
                                            
                                            <div className="detail-grid">
                                                <div className="detail-section">
                                                    <h5>Basic Information</h5>
                                                    <div className="detail-item">
                                                        <span className="detail-label">Name:</span>
                                                        <span className="detail-value">{getInstitutionDisplayName(institution)}</span>
                                                    </div>
                                                </div>

                                                {institution.totalAvailability && institution.totalAvailability.length > 0 && (
                                                    <div className="detail-section">
                                                        <h5>Availability Slots</h5>
                                                        <div className="availability-display">
                                                            {institution.totalAvailability.map((num, index) => (
                                                                <span key={index} className="availability-chip">{num}</span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {institution.groups && institution.groups.length > 0 && (
                                                    <div className="detail-section">
                                                        <h5>Groups/Tags</h5>
                                                        <div className="groups-display">
                                                            {institution.groups.map((group, index) => (
                                                                <span key={index} className="group-chip">{group}</span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {institution.subInstitutions && institution.subInstitutions.length > 0 && (
                                                    <div className="detail-section">
                                                        <h5>Sub-Institutions</h5>
                                                        <div className="groups-display">
                                                            {institution.subInstitutions.map((subInst, index) => (
                                                                <span key={index} className="group-chip sub-institution-chip">{subInst}</span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                    {!loadingInstitutions && institutions.length === 0 && (
                        <div className="empty-state">
                            <div className="empty-icon">🏢</div>
                            <p>No institutions found. Create your first institution above.</p>
                        </div>
                    )}
                    </>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <div className="universal-white-container speciality-institution-manager">
            <div className="modern-schedule-header">
                <h2>Rotation Type &amp; Institution Manager</h2>
                <p>Create and manage Rotation Types and institutions</p>
            </div>

            {/* Success/Error Messages */}
            {success && (
                <div className="alert alert-success">
                    <span className="alert-icon">✅</span>
                    {success}
                </div>
            )}

            {error && (
                <div className="alert alert-error">
                    <span className="alert-icon">⚠️</span>
                    {error}
                </div>
            )}

            {/* Tab Navigation */}
            <div className="tab-navigation">
                <button
                    className={`tab-btn ${activeTab === 'specialities' ? 'active' : ''}`}
                    onClick={() => setActiveTab('specialities')}
                >
                    <span className="tab-icon">🏥</span>
                                                Rotations/Experiences
                </button>
                <button
                    className={`tab-btn ${activeTab === 'institutions' ? 'active' : ''}`}
                    onClick={() => setActiveTab('institutions')}
                >
                    <span className="tab-icon">🏢</span>
                    Institutions
                </button>
            </div>

            {/* Tab Content */}
            {activeTab === 'specialities' ? renderSpecialitiesTab() : renderInstitutionsTab()}
            
            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div style={{
                    position: 'fixed', inset: 0, background: deleteWarning ? 'rgba(9, 26, 43, 0.18)' : 'rgba(9, 26, 43, 0.12)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.25rem', zIndex: 3000,
                }}>
                    <div style={{
                        width: 'min(620px, 100%)', background: 'linear-gradient(180deg,#ffffff 0%,#f7fbff 100%)',
                        borderRadius: 20, overflow: 'hidden',
                        border: deleteWarning ? '1px solid rgba(193, 212, 233, 0.95)' : '1px solid rgba(19, 39, 64, 0.22)',
                        boxShadow: deleteWarning ? '0 14px 34px rgba(22, 54, 86, 0.12)' : '0 18px 42px rgba(9, 26, 43, 0.18)'
                    }}>
                        {deleteWarning ? (
                            <div style={{ padding: '1.35rem 1.5rem', background: 'linear-gradient(135deg,#f59e0b,#fbbf24)', borderBottom: '1px solid rgba(202,138,4,0.22)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                    <div style={{ width: 42, height: 42, borderRadius: '14px', background: 'rgba(255,255,255,0.22)', color: '#7c2d12', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.05rem', fontWeight: 800, border: '1px solid rgba(255,255,255,0.35)' }}>
                                        <FaExclamationTriangle />
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '0.78rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#7c2d12' }}>Replacement required</div>
                                        <h3 style={{ margin: '0.15rem 0 0', fontSize: '1.2rem', color: '#4a2508' }}>Affected preceptors need a replacement</h3>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div style={{ padding: '1.35rem 1.5rem', background: 'linear-gradient(135deg,#173f69,#255886)', borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                    <div style={{ width: 42, height: 42, borderRadius: '14px', background: 'rgba(255,255,255,0.14)', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.05rem', fontWeight: 800, border: '1px solid rgba(255,255,255,0.2)' }}>
                                        <FaTrash />
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '0.78rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.78)' }}>Confirm deletion</div>
                                        <h3 style={{ margin: '0.15rem 0 0', fontSize: '1.2rem', color: '#ffffff' }}>Delete this {deleteType}</h3>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div style={{ padding: '1.4rem 1.5rem 1.5rem' }}>
                            <p style={{ margin: '0 0 1rem', color: '#475569', lineHeight: 1.6 }}>
                                {deleteWarning
                                    ? `This ${deleteType} is still assigned to ${deleteWarning.affected_count || 0} preceptor(s). Choose a replacement and continue.`
                                    : `Are you sure you want to delete this ${deleteType}?`}
                            </p>

                            <div style={{
                                background: 'linear-gradient(180deg,#ffffff 0%,#f8fbff 100%)',
                                border: '1px solid rgba(193, 212, 233, 0.9)',
                                borderRadius: 16,
                                padding: '1rem 1.1rem',
                                marginBottom: '1rem'
                            }}>
                                <div style={{ fontSize: '0.76rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.55rem' }}>Selected item</div>
                                <div style={{ color: '#15324f', fontWeight: 700, fontSize: '0.98rem' }}>
                                    {deleteType === 'speciality' ? getSpecialityDisplayName(itemToDelete) : getInstitutionDisplayName(itemToDelete)}
                                </div>
                            </div>

                            {deleteWarning ? (
                                <>
                                    {Array.isArray(deleteWarning.affected_preceptors) && deleteWarning.affected_preceptors.length > 0 && (
                                        <div style={{
                                            background: 'linear-gradient(180deg,#ffffff 0%,#f8fbff 100%)',
                                            border: '1px solid rgba(193, 212, 233, 0.9)',
                                            borderRadius: 16,
                                            padding: '1rem 1.1rem',
                                            marginBottom: '1rem'
                                        }}>
                                            <div style={{ fontSize: '0.76rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.55rem' }}>Affected preceptors</div>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                                {deleteWarning.affected_preceptors.slice(0, 8).map((preceptor) => (
                                                    <span key={preceptor.name} style={{
                                                        background: '#eef5fb', border: '1px solid #c9d9e8', color: '#15324f',
                                                        borderRadius: 999, padding: '0.35rem 0.7rem', fontSize: '0.85rem', fontWeight: 600
                                                    }}>
                                                        {preceptor.name}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <div className="form-group" style={{ marginTop: '1rem', textAlign: 'left' }}>
                                        <label className="form-label">
                                            Replacement {deleteType === 'speciality' ? 'Rotation Type' : 'Institution'}
                                        </label>
                                        <select
                                            className="form-input"
                                            value={replacementId}
                                            onChange={(e) => setReplacementId(e.target.value)}
                                        >
                                            <option value="">Select a replacement...</option>
                                            {getReplacementOptions().map((option) => (
                                                <option key={option._id} value={option._id}>
                                                    {deleteType === 'speciality'
                                                        ? getSpecialityDisplayName(option)
                                                        : getInstitutionDisplayName(option)}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </>
                            ) : (
                                <p style={{ margin: 0, color: '#64748b', fontSize: '0.93rem' }}>This action cannot be undone.</p>
                            )}

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', flexWrap: 'wrap', marginTop: '1.25rem' }}>
                                <button
                                    onClick={cancelDelete}
                                    disabled={loading}
                                    type="button"
                                    style={{
                                        padding: '0.8rem 1.2rem',
                                        borderRadius: 12,
                                        border: '1px solid rgba(193, 212, 233, 0.95)',
                                        background: 'white',
                                        color: '#355a80',
                                        fontWeight: 700,
                                        cursor: loading ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmDelete}
                                    type="button"
                                    disabled={loading || (deleteWarning && !replacementId)}
                                    style={{
                                        padding: deleteWarning ? '0.8rem 1.3rem' : '0.8rem 1.4rem',
                                        borderRadius: 12,
                                        border: 'none',
                                        background: deleteWarning ? 'linear-gradient(135deg,#dc2626,#ef4444)' : 'linear-gradient(135deg,#173f69,#255886)',
                                        color: 'white'
                                        , fontWeight: 800,
                                        cursor: loading || (deleteWarning && !replacementId) ? 'not-allowed' : 'pointer',
                                        boxShadow: deleteWarning ? '0 10px 22px rgba(220,38,38,0.22)' : '0 4px 14px rgba(23,63,105,0.35)'
                                    }}
                                >
                                    {loading ? 'Deleting...' : deleteWarning ? 'Continue with replacement' : 'Delete'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SpecialityInstitutionManager;
